import { Component, computed, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import html2canvas from 'html2canvas';
import { CommonModule, NgClass } from '@angular/common';
import { jsPDF } from 'jspdf';
import { Subscription } from 'rxjs';

import { SummaryReportComponent } from './summary-report/summary-report.component';
import { ReportLayoutComponent } from './report-layout/report-layout.component';
import { InteractionReportComponent } from './interaction-report/interaction-report.component';
import { CustomerReportService, OverviewReportRequestData } from '../../services/customer-report.service';
import { FilterService } from '../../services/filter.service';
import { BottomSheetDataService } from '../../services/bottom-sheet-data.service';
import { InteractionPage, SummaryPage, SummaryPageChartData } from './report.model';
import { FilterRequest } from '../../shared/types';
import { delayLoading, transformTestToFilters } from '../../shared/appUtil';
import { SharedDataService } from '../../services/shared-data.service';
import { CustomerReportApiService } from '../../services/api-services/customer-report-api.service';
import { NotificationService } from '../../services/notification.service';

@Component({
  selector: 'report',
  standalone: true,
  imports: [
    ReportLayoutComponent,
    InteractionReportComponent,
    SummaryReportComponent,
    MatIconModule,
    NgClass,
    CommonModule,
    FormsModule,
  ],
  templateUrl: './report.component.html',
  styleUrl: './report.component.scss',
})
export class ReportComponent {
  constructor(
    private filterService: FilterService,
    private customerReportService: CustomerReportService,
    private customerReportApiService: CustomerReportApiService,
    private sharedDataService: SharedDataService,
    public notificationService: NotificationService
  ) {}

  private _bottomSheetDataService = inject(BottomSheetDataService);
  @ViewChild('rightContent') rightContent!: ElementRef<HTMLDivElement>;
  protected readonly interactionPage = computed(() => this.interactionPages()[this.selectedIndex()]);
  hideInfoBoxForever = computed(() => this.customerReportService.hideInfoMsg());
  private subscription: Subscription | undefined;
  protected customerEndpointAddressSelected = computed<boolean>(() =>
    this.filterService.customerEndpointAddressSelected()
  );
  _tempInput = '';
  _characterLimit = 50;
  _hideInfoBoxForever: boolean = false;
  _fetchingApiData = true;
  interactionPages = signal<Array<InteractionPage>>([]);
  selectedIndex = signal<number>(-1);
  summaryPage = signal<SummaryPage>({} as SummaryPage);
  inputName = 'Report';
  inputFocused: boolean = false;
  pdfLoading: boolean = false;
  hideInfoBox: boolean = false;
  disabled = true;
  chartData: SummaryPageChartData = { channelData: [], resolutionData: [] };
  reportRequestData: OverviewReportRequestData = {} as OverviewReportRequestData;

  ngOnInit() {
    this.subscription = this.sharedDataService.getDateRange().subscribe((date) => {
      this.reportRequestData.start_date = date.startAPIDate;
      this.reportRequestData.end_date = date.endAPIDate;
    });

    this.fetchReportData();
  }

  async fetchReportData() {
    this.reportRequestData.filters = this.populateFilteredData();
    this.reportRequestData.timeZone = this.sharedDataService.selectedTimezoneValue;

    await delayLoading(1);

    this.customerReportApiService.getOverviewReport(this.reportRequestData).subscribe({
      next: (data) => {
        this.interactionPages.set(this.customerReportService.createInteractionPages(data));
        this.summaryPage.set(this.customerReportService.createSummaryPage(data));
        this.chartData.channelData = this.customerReportService.transformDataToPieChartData(
          data.channelDistribution.channels,
          data.channelDistribution.channelPercentages
        );
        this.chartData.resolutionData = this.customerReportService.transformDataToPieChartData(
          data.resolutionDistribution.resolutions,
          data.resolutionDistribution.resolutionPercentages
        );

        this._fetchingApiData = false;
      },
      error: (error) => {
        this._fetchingApiData = false;
        console.error('API call failed:', error);
      },
      complete: () => {},
    });
  }

  populateFilteredData(): FilterRequest[] {
    const filterDataArray = this.filterService.getFilterConditionDataValue();
    const { data, error } = transformTestToFilters(filterDataArray);
    const filterApplied = this.filterService.getFilterAppliedStatusValue();

    const filteredData: FilterRequest[] = [...data];
    if (error && filterApplied) {
      this.notificationService.error('Error in filters', 'Error in filters');
    }
    return filteredData;
  }

  scrollToDescription(index: number): void {
    let element = this.rightContent.nativeElement.querySelector(`#page${index}`);
    if (index === -1) {
      element = this.rightContent.nativeElement.querySelector(`#summaryPage`);
    }
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  handleInfoClosePress() {
    if (this._hideInfoBoxForever) {
      this.customerReportService.setHideInfoMsg(true);
    } else {
      this.hideInfoBox = true;
    }
  }

  handleSavePress() {
    this.inputName = this._tempInput;
    this.disabled = true;
  }

  checkValue(event: any) {
    if (
      event.target.value == this.inputName ||
      event.target.value.length > this._characterLimit ||
      event.target.value.length === 0
    ) {
      this.disabled = true;
    } else {
      this.disabled = false;
    }
    this._tempInput = event.target.value;
  }

  handleCheckBoxPress() {
    this._hideInfoBoxForever = !this._hideInfoBoxForever;
  }

  async capturePages(): Promise<void> {
    if (this.inputName.trim().length === 0) {
      return;
    }
    this.pdfLoading = true;

    const pageIds = ['summaryPage', ...this.interactionPages().map((_, index) => `page${index}`)];
    const pdf = new jsPDF('p', 'px', [612, 792], true);

    for (let i = 0; i < pageIds.length; i++) {
      const element = document.getElementById(pageIds[i]) as HTMLElement;
      if (element) {
        await html2canvas(element, { scale: 2 })
          .then((canvas) => {
            const imgData = canvas.toDataURL('image/png');
            // Add each image to a new page, but avoid adding a page before the first image
            if (i > 0) {
              pdf.addPage();
            }
            pdf.addImage(imgData, 'PNG', 0, 0, 612, 792, undefined, 'FAST');
          })
          .catch((error) => {
            console.error('Error capturing element with id:', pageIds[i], error);
          });
      } else {
        console.error('Element not found with id:', pageIds[i]);
      }
    }
    pdf.save(`${this.inputName}.pdf`);
    this.pdfLoading = false;
  }

  handlePreviewPress(index: number, type = 'interaction') {
    if (type === 'interaction') {
      this.selectedIndex.set(index);
    } else {
      this.selectedIndex.set(-1);
    }
    this.scrollToDescription(index);
  }

  handleChooseFilterPress() {
    this._bottomSheetDataService.setBottomSheetExpandedState('minimized');
    this.filterService.handleCustomerEndpointAddress();
  }
}
